/**
 * Segments — saved audience filters, with a live preview.
 *
 * The available fields and their types come from the backend
 * (GET /mailing/segments/fields) rather than being listed here: the operator
 * set depends on the type, and the backend's whitelist is what actually decides
 * whether a condition survives. A local copy would drift on the first change.
 */
define('mailingSegmentsView',[
  'module',
  'app',
  'underscore',
  'jquery',
  'marionette',
  'mailingShell',
  'entities/mailing'
], function (
  module,
  App,
  _,
  $,
  Marionette,
  Shell,
  MailingApi
) {
  'use strict';

  /** Operators offered per field type — must stay a subset of the backend's. */
  var OPERATORS = {
    text: [
      { key: 'equals', label: 'is' },
      { key: 'not_equals', label: 'is not' },
      { key: 'contains', label: 'contains' },
      { key: 'not_contains', label: 'does not contain' },
      { key: 'starts_with', label: 'starts with' },
      { key: 'is_empty', label: 'is empty', noValue: true },
      { key: 'is_not_empty', label: 'is not empty', noValue: true }
    ],
    number: [
      { key: 'equals', label: '=' },
      { key: 'not_equals', label: '≠' },
      { key: 'gt', label: '>' },
      { key: 'gte', label: '≥' },
      { key: 'lt', label: '<' },
      { key: 'lte', label: '≤' }
    ],
    'boolean': [
      { key: 'is_true', label: 'is yes', noValue: true },
      { key: 'is_false', label: 'is no', noValue: true }
    ],
    date: [
      { key: 'after', label: 'after', valueType: 'date' },
      { key: 'before', label: 'before', valueType: 'date' },
      { key: 'within_last', label: 'within the last', valueType: 'duration' },
      { key: 'more_than_ago', label: 'more than … ago', valueType: 'duration' },
      { key: 'is_empty', label: 'is never', noValue: true },
      { key: 'is_not_empty', label: 'is set', noValue: true }
    ]
  };

  var SegmentsView = Marionette.ItemView.extend({

    className: 'mailing-page mailing-segments',

    template: _.template(
      '<%= nav %>' +
      '<div class="mlg-body"><div class="mlg-loading">Loading segments…</div></div>' +
      '<div class="mlg-modal" style="display:none"></div>'
    ),

    ui: {
      body: '.mlg-body',
      modal: '.mlg-modal'
    },

    events: {
      'click .mlg-new-segment': 'onNew',
      'click .mlg-edit': 'onEdit',
      'click .mlg-delete': 'onDelete',
      'click .mlg-modal-cancel': 'closeModal',
      'click .mlg-modal-save': 'onSave',
      'click .mlg-add-condition': 'onAddCondition',
      'click .mlg-remove-condition': 'onRemoveCondition',
      'change .f-field': 'onFieldChanged',
      'change .f-operator': 'onOperatorChanged',
      'change .f-logic': 'schedulePreview',
      'change .c-value, .c-unit': 'schedulePreview',
      'keyup .c-value': 'schedulePreview'
    },

    serializeData: function () {
      return {
        nav: Shell.navHtml('segments',
          '<button type="button" class="mlg-btn primary mlg-new-segment">New segment</button>')
      };
    },

    onShow: function () {
      this.load();
    },

    load: function () {
      var self = this;
      $.when(MailingApi.segments(), MailingApi.segmentFields())
        .done(function (segments, fields) {
          self.fields = fields || [];
          self.renderList(segments || []);
        })
        .fail(function (xhr) {
          self.ui.body.html('<div class="mlg-error">' +
            _.escape(MailingApi.errorMessage(xhr)) + '</div>');
        });
    },

    renderList: function (segments) {
      if (!segments.length) {
        this.ui.body.html(Shell.emptyHtml(
          'No segment yet. A segment is the audience a campaign or a workflow sends to.'));
        return;
      }
      var rows = _.map(segments, function (segment) {
        return '<tr data-secid="' + segment.secId + '">' +
          '<td class="c-name">' + _.escape(segment.name || '') +
            '<div class="c-sub">' + _.escape(segment.description || '') + '</div></td>' +
          '<td class="num">' + Shell.formatNumber(segment.subscriberCount) + '</td>' +
          '<td class="c-actions">' +
          '  <button type="button" class="mlg-btn small mlg-edit">Edit</button>' +
          '  <button type="button" class="mlg-btn small danger mlg-delete">Delete</button>' +
          '</td></tr>';
      }).join('');

      this.ui.body.html(
        '<table class="mlg-table">' +
        '<thead><tr><th>Segment</th>' +
        '<th class="num" title="Active contacts at the last hourly refresh. A send always ' +
          'resolves its audience live, so this number is indicative.">Contacts</th>' +
        '<th></th></tr></thead>' +
        '<tbody>' + rows + '</tbody></table>');
    },

    //----------Editor-------------------------------------------------------//

    onNew: function () {
      this.openEditor({ filters: '{"logic":"AND","conditions":[]}' });
    },

    onEdit: function (e) {
      var self = this;
      MailingApi.segment(this.secIdOf(e)).done(function (segment) {
        self.openEditor(segment);
      });
    },

    openEditor: function (segment) {
      var filters = this.parseFilters(segment.filters);
      this.editingSecId = segment.secId || null;

      this.ui.modal.html(
        '<div class="mlg-modal-card wide">' +
        '<h3>' + (segment.secId ? 'Edit segment' : 'New segment') + '</h3>' +
        '<div class="mlg-row">' +
        '  <label>Name<input type="text" class="f-name" value="' + _.escape(segment.name || '') + '"></label>' +
        '  <label>Description<input type="text" class="f-description" value="' +
             _.escape(segment.description || '') + '"></label>' +
        '</div>' +
        '<div class="mlg-filter-head">' +
        '  <label class="inline">Match<select class="f-logic">' +
        '    <option value="AND"' + (filters.logic === 'OR' ? '' : ' selected') + '>all conditions</option>' +
        '    <option value="OR"' + (filters.logic === 'OR' ? ' selected' : '') + '>any condition</option>' +
        '  </select></label>' +
        '  <button type="button" class="mlg-btn small mlg-add-condition">Add condition</button>' +
        '</div>' +
        '<div class="mlg-conditions"></div>' +
        '<div class="mlg-preview">Preview: <span class="p-count">—</span></div>' +
        '<div class="mlg-preview-sample"></div>' +
        '<div class="mlg-modal-actions">' +
        '  <button type="button" class="mlg-btn mlg-modal-cancel">Cancel</button>' +
        '  <button type="button" class="mlg-btn primary mlg-modal-save">Save</button>' +
        '</div>' +
        '</div>').show();

      var $conditions = this.ui.modal.find('.mlg-conditions');
      _.each(filters.conditions, function (condition) {
        $conditions.append(this.conditionHtml(condition));
      }, this);

      this.schedulePreview();
    },

    parseFilters: function (raw) {
      try {
        var parsed = JSON.parse(raw || '{}');
        return {
          logic: parsed.logic === 'OR' ? 'OR' : 'AND',
          conditions: _.isArray(parsed.conditions) ? parsed.conditions : []
        };
      } catch (error) {
        return { logic: 'AND', conditions: [] };
      }
    },

    conditionHtml: function (condition) {
      var current = condition || {};
      var fieldName = current.field || (this.fields.length ? this.fields[0].name : '');
      var field = _.findWhere(this.fields, { name: fieldName }) || this.fields[0] || { type: 'text' };

      var fieldOptions = _.map(this.fields, function (item) {
        return '<option value="' + item.name + '"' + (item.name === fieldName ? ' selected' : '') + '>' +
          _.escape(Shell.prettyCase(item.name)) + '</option>';
      }).join('');

      return '<div class="mlg-condition" data-type="' + field.type + '">' +
        '<select class="f-field">' + fieldOptions + '</select>' +
        '<select class="f-operator">' + this.operatorOptions(field.type, current.operator) + '</select>' +
        this.valueHtml(field.type, current.operator, current.value, current.unit) +
        '<button type="button" class="mlg-icon-btn mlg-remove-condition" title="Remove">×</button>' +
        '</div>';
    },

    operatorOptions: function (type, selected) {
      return _.map(OPERATORS[type] || OPERATORS.text, function (operator) {
        return '<option value="' + operator.key + '"' +
          (operator.key === selected ? ' selected' : '') + '>' +
          _.escape(operator.label) + '</option>';
      }).join('');
    },

    /** The value input depends on the operator: none, a date, a number, or a duration. */
    valueHtml: function (type, operatorKey, value, unit) {
      var operators = OPERATORS[type] || OPERATORS.text;
      var operator = _.findWhere(operators, { key: operatorKey }) || operators[0];
      if (!operator || operator.noValue) {
        return '<span class="c-value-slot"></span>';
      }
      if (operator.valueType === 'date') {
        return '<span class="c-value-slot"><input type="date" class="c-value" value="' +
          _.escape(value || '') + '"></span>';
      }
      if (operator.valueType === 'duration') {
        var units = ['days', 'weeks', 'months'];
        var unitOptions = _.map(units, function (item) {
          return '<option value="' + item + '"' + (item === unit ? ' selected' : '') + '>' + item + '</option>';
        }).join('');
        return '<span class="c-value-slot"><input type="number" min="1" class="c-value narrow" value="' +
          _.escape(value || '1') + '"><select class="c-unit">' + unitOptions + '</select></span>';
      }
      return '<span class="c-value-slot"><input type="' + (type === 'number' ? 'number' : 'text') +
        '" class="c-value" value="' + _.escape(value === undefined ? '' : value) + '"></span>';
    },

    onAddCondition: function () {
      this.ui.modal.find('.mlg-conditions').append(this.conditionHtml(null));
      this.schedulePreview();
    },

    onRemoveCondition: function (e) {
      $(e.currentTarget).closest('.mlg-condition').remove();
      this.schedulePreview();
    },

    /** Field change resets the operator list — an operator rarely survives a type change. */
    onFieldChanged: function (e) {
      var $condition = $(e.currentTarget).closest('.mlg-condition');
      var field = _.findWhere(this.fields, { name: $condition.find('.f-field').val() }) || { type: 'text' };
      $condition.attr('data-type', field.type);
      $condition.find('.f-operator').html(this.operatorOptions(field.type, null));
      $condition.find('.c-value-slot').replaceWith(
        this.valueHtml(field.type, $condition.find('.f-operator').val(), '', 'days'));
      this.schedulePreview();
    },

    onOperatorChanged: function (e) {
      var $condition = $(e.currentTarget).closest('.mlg-condition');
      var type = $condition.attr('data-type') || 'text';
      $condition.find('.c-value-slot').replaceWith(
        this.valueHtml(type, $condition.find('.f-operator').val(), '', 'days'));
      this.schedulePreview();
    },

    //----------Preview------------------------------------------------------//

    collectFilters: function () {
      var conditions = [];
      this.ui.modal.find('.mlg-condition').each(function () {
        var $condition = $(this);
        var condition = {
          field: $condition.find('.f-field').val(),
          operator: $condition.find('.f-operator').val()
        };
        var $value = $condition.find('.c-value');
        if ($value.length) {
          condition.value = $value.val();
        }
        var $unit = $condition.find('.c-unit');
        if ($unit.length) {
          condition.unit = $unit.val();
        }
        conditions.push(condition);
      });
      return {
        logic: this.ui.modal.find('.f-logic').val() || 'AND',
        conditions: conditions
      };
    },

    /** Debounced: the builder fires on every keystroke inside a value box. */
    schedulePreview: function () {
      var self = this;
      if (this.previewTimer) {
        window.clearTimeout(this.previewTimer);
      }
      this.previewTimer = window.setTimeout(function () { self.refreshPreview(); }, 350);
    },

    refreshPreview: function () {
      var $modal = this.ui.modal;
      if (!$modal.is(':visible')) { return; }

      MailingApi.previewSegment(JSON.stringify(this.collectFilters())).done(function (preview) {
        $modal.find('.p-count').text(Shell.formatNumber(preview.count) + ' contact(s)');
        var sample = preview.sample || [];
        $modal.find('.mlg-preview-sample').html(!sample.length ? '' :
          '<table class="mlg-table compact"><tbody>' + _.map(sample, function (subscriber) {
            var name = ((subscriber.firstName || '') + ' ' + (subscriber.lastName || '')).trim();
            return '<tr><td>' + _.escape(name || '—') + '</td>' +
              '<td>' + _.escape(subscriber.email || '') + '</td>' +
              '<td class="num">' + Shell.formatMoney(subscriber.totalTurnover) + '</td>' +
              '<td>' + Shell.formatDate(subscriber.lastOrder) + '</td></tr>';
          }).join('') + '</tbody></table>');
      }).fail(function (xhr) {
        $modal.find('.p-count').text(MailingApi.errorMessage(xhr));
      });
    },

    //----------Save / delete------------------------------------------------//

    onSave: function () {
      var self = this;
      var data = {
        name: this.ui.modal.find('.f-name').val(),
        description: this.ui.modal.find('.f-description').val(),
        filters: JSON.stringify(this.collectFilters())
      };
      if (!data.name) {
        Shell.banner(this.$el, 'Name is required', 'error');
        return;
      }
      var saving = this.editingSecId ?
          MailingApi.updateSegment(this.editingSecId, data) :
          MailingApi.createSegment(data);

      saving.done(function () {
        self.closeModal();
        Shell.banner(self.$el, 'Segment saved', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    onDelete: function (e) {
      var self = this;
      var name = $(e.currentTarget).closest('tr').find('.c-name').text();
      if (!window.confirm('Delete this segment? Campaigns using it lose their audience.\n\n' + name)) { return; }

      MailingApi.deleteSegment(this.secIdOf(e)).done(function () {
        Shell.banner(self.$el, 'Segment deleted', 'ok');
        self.load();
      }).fail(function (xhr) {
        Shell.banner(self.$el, MailingApi.errorMessage(xhr), 'error');
      });
    },

    closeModal: function () {
      this.editingSecId = null;
      this.ui.modal.hide().empty();
    },

    secIdOf: function (e) {
      return $(e.currentTarget).closest('tr').data('secid');
    }
  });

  module.exports = SegmentsView;
});

